/**
 * Common-panel theme rail — 8 glyph chips that swap the Common tile
 * grid between STEM themes. Spec mirrored from Claude Design's
 * handoff:
 *
 *   lilia-design/ThemePickerHandoff/design_handoff_theme_picker/README.md
 *
 * Layout: a single horizontal row, 48 px tall, sitting between the
 * tab strip and the search input. Inactive chips are glyph-only and
 * 36×32. The active chip expands inline to carry its short label
 * (`∫ Calculus`). Width animates with `cubic-bezier(0.2, 0.8, 0.2, 1)`
 * over 200 ms; background and colour transitions are 140 ms linear.
 *
 * Keyboard: `←` / `→` cycle, `Home` / `End` jump to first/last.
 * `Tab` deliberately leaves the rail — drawer-tab cycling owns Tab
 * at the parent level. `Enter` / `Space` on the active chip is a noop.
 *
 * First-mount pulse: a single 1.6 s outline animation on the rail's
 * outer container, gated by `lilia.math.themeRail.pulseSeen` in
 * localStorage. We pulse the container — not a single chip — because
 * the affordance is "this whole control switches themes," not "click
 * this specific theme."
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { hasSeenPulse, markPulseSeen, THEMES, type ThemeId } from "./commonThemes";

interface CommonThemeRailProps {
  activeTheme: ThemeId;
  onChange: (theme: ThemeId) => void;
  /** Compact mode shrinks chips to 32×30 for the block-inline drawer. */
  density?: "normal" | "compact";
}

export function CommonThemeRail({ activeTheme, onChange, density = "normal" }: CommonThemeRailProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Map<ThemeId, HTMLButtonElement | null>>(new Map());
  const [shouldPulse, setShouldPulse] = useState<boolean>(false);

  useEffect(() => {
    if (hasSeenPulse()) return;
    setShouldPulse(true);
    markPulseSeen();
    const t = window.setTimeout(() => setShouldPulse(false), 1700);
    return () => window.clearTimeout(t);
  }, []);

  const focusTheme = useCallback((id: ThemeId) => {
    const el = chipRefs.current.get(id);
    el?.focus();
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const idx = THEMES.findIndex((t) => t.id === activeTheme);
      if (idx < 0) return;
      let nextIdx = idx;
      if (e.key === "ArrowLeft") nextIdx = (idx - 1 + THEMES.length) % THEMES.length;
      else if (e.key === "ArrowRight") nextIdx = (idx + 1) % THEMES.length;
      else if (e.key === "Home") nextIdx = 0;
      else if (e.key === "End") nextIdx = THEMES.length - 1;
      else return;
      e.preventDefault();
      e.stopPropagation();
      const nextTheme = THEMES[nextIdx];
      onChange(nextTheme.id);
      focusTheme(nextTheme.id);
    },
    [activeTheme, onChange, focusTheme],
  );

  const inactiveW = density === "compact" ? 32 : 36;
  const inactiveH = density === "compact" ? 30 : 32;
  const activeH = density === "compact" ? 30 : 32;
  const chipGap = 4;

  return (
    <div
      ref={railRef}
      role="tablist"
      aria-label="Common panel theme"
      onKeyDown={onKeyDown}
      style={{
        padding: density === "compact" ? "6px 10px" : "8px 12px",
        background: "var(--lilia-surface)",
        borderBottom: "1px solid var(--lilia-border)",
        display: "flex",
        alignItems: "center",
        gap: chipGap,
        overflowX: "auto",
        whiteSpace: "nowrap",
        scrollbarWidth: "thin",
        position: "relative",
        animation: shouldPulse ? "mc-pulse 1.6s ease-out 1" : undefined,
        borderRadius: shouldPulse ? 8 : 0,
      }}
    >
      {THEMES.map((t) => {
        const isActive = t.id === activeTheme;
        return (
          <button
            key={t.id}
            ref={(el) => {
              chipRefs.current.set(t.id, el);
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={t.name}
            title={isActive ? undefined : `${t.name} — ${t.tip}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(t.id)}
            className="lilia-theme-chip"
            data-testid={`math-theme-chip-${t.id}`}
            data-active={isActive ? "1" : "0"}
            style={{
              flexShrink: 0,
              height: isActive ? activeH : inactiveH,
              width: isActive ? "auto" : inactiveW,
              minWidth: isActive ? undefined : inactiveW,
              padding: isActive ? "0 12px 0 9px" : 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              background: isActive ? "var(--lilia-primary)" : "transparent",
              border: "1px solid transparent",
              borderRadius: 6,
              color: isActive ? "#fff" : "var(--lilia-text-secondary)",
              boxShadow: isActive ? "0 1px 2px color-mix(in srgb, var(--lilia-primary) 45%, transparent)" : "none",
              transition:
                "width 200ms cubic-bezier(0.2,0.8,0.2,1), background 140ms linear, color 140ms linear, box-shadow 140ms linear, border-color 140ms linear",
              cursor: "pointer",
              outline: "none",
              fontFamily: t.font === "mono"
                ? "var(--lilia-font-mono, ui-monospace, monospace)"
                : "var(--lilia-font-serif, 'IBM Plex Serif', Georgia, serif)",
              fontStyle: t.style === "italic" ? "italic" : "normal",
              fontWeight: t.weight ?? (isActive ? 600 : 500),
              fontSize: density === "compact" ? 15 : 16,
              lineHeight: 1,
            }}
          >
            <span aria-hidden style={{ display: "inline-flex", alignItems: "center" }}>{t.glyph}</span>
            {isActive && (
              <span
                style={{
                  fontFamily: "var(--lilia-font-sans, system-ui, sans-serif)",
                  fontStyle: "normal",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "-0.005em",
                }}
              >
                {t.chip}
              </span>
            )}
          </button>
        );
      })}

      <style>{`
        @keyframes mc-pulse {
          0%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--lilia-primary) 45%, transparent); }
          70%  { box-shadow: 0 0 0 8px transparent; }
          100% { box-shadow: 0 0 0 0 transparent; }
        }
        .lilia-math-editor .lilia-theme-chip[data-active="0"]:hover {
          background: var(--lilia-bg-muted) !important;
          color: var(--lilia-text) !important;
        }
        .lilia-math-editor .lilia-theme-chip:focus-visible {
          outline: 2px solid color-mix(in srgb, var(--lilia-primary) 60%, transparent);
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
